import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ErrorHandlerService } from '../core/error-handler';
import { UserService } from '../core/user';
import { AnalyticsService } from '../core/analytics';
import { ServiceNowCase } from '../models/recover/ServiceNowCase';
import { ReassignPayload } from '../models/recover/ReassignPayload';
import { UserLocation } from '../models/user/location.model';
import { CareConstants } from './care.constants';
import { environment } from '../../environments/environment';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActionSelectionDialogComponent } from './action-selection-dialog/action-selection-dialog.component';
import * as care from '../actions/care';
import { Store } from '@ngrx/store';
import * as fromRoot from '../reducers';
import { Router } from '@angular/router';

@Injectable()
export class CareService {
  constructor(private http: HttpClient,
              private ehs: ErrorHandlerService,
              private analytics: AnalyticsService,
              private us: UserService,
              private dialog: MatDialog,
              private store: Store<fromRoot.State>,
              private router: Router) { }

  public getCareReports(request) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/cases`;
        return this.http.post(environment.spotlightApiUrl + url, request);
      }),
      map((res: any) => res.responseObject),
      catchError((error) => {
        return this.ehs.handleError(error);
      })
    );
  }

  public getCareStatusOptions() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/status-options`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public acknowledgeReport(report: ServiceNowCase) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/case/acknowledge`;
        return this.http.patch(environment.spotlightApiUrl + url, report);
      }),
      catchError(this.ehs.handleError)
    );
  }

  public reassignReport(payload: ReassignPayload) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/case/reassign`;
        return this.http.patch(environment.spotlightApiUrl + url, payload);
      }),
      catchError(this.ehs.handleError)
    );
  }

  public getReportDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/case/${id}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getReportActivityHistory(report) {
    const { id, reportDateTime, cfaId, incidentDate, cfaid } = report;
    const payload = {
      incidentDate: incidentDate ? incidentDate : reportDateTime,
      cfaId: cfaId ? cfaId : cfaid,
      incidentId: id
    };
    report.cfaId = report.cfaId? report.cfaId : report.cfaid;
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/ticket/history`;
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCareEmailDetails(id) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/ticket/history/email/${id}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError(this.ehs.handleError)
    );
  }

  public getCareTemplate(request) {
    const { incidentId, type, memberType } = request;
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${incidentId}/`
          + `${memberType}/response/templates?type=${type}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => {
        return res.responseObject;
      }),
      catchError((error) => {
        return this.ehs.handleError(error)
      })
    );
  }

  public getSpotCustomerCareTemplate(type) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/response/templates?type=${type}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((error) => {
        return this.ehs.handleError(error)
      })
    );
  }

  public updateIncident(request) {
    const { incidentId, cfaId, resolutionStatus, type, note } = request;
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${incidentId}/${cfaId}/response?type=${type}`;
        return this.http.post(environment.spotlightApiUrl + url, { resolutionStatus, note });
      }),
      map((res) => res),
      catchError(this.ehs.handleError)
    );
  }

  public sendCareResponse(payload) {
    const { incidentId, cfaId, includeReward, careCopyChanged, offerTemplateIds, isP1 } = payload;
    const type = includeReward ? CareConstants.rewardType : CareConstants.emailType;

    this.analytics.sendEvent(
      'Response sent',
      { includeReward, careCopyChanged, offerTemplateIds }
    );

    if (isP1) {
      this.analytics.sendEvent(
        includeReward ? 'P1 Reward Sent' : 'P1 Email Sent',
        { incidentId, offerTemplateIds }
      );
    }
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${incidentId}/${cfaId}/response?type=${type}`;
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map(() => incidentId),
      catchError((error) => {
        return this.ehs.handleError(error)
      })
    );
  }

  public sendSpotCareResponse(payload) {
    const { cfaId, includeReward, careCopyChanged, offerTemplateIds } = payload;
    const type = includeReward ? CareConstants.rewardType : CareConstants.emailType;

    this.analytics.sendEvent(
      'Spot Response sent',
      { includeReward, careCopyChanged, offerTemplateIds, cfaId }
    );
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/response/${cfaId}?type=${type}`;
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map(() => {
        return {
          customerFirstName: payload.firstName ? payload.firstName : '',
          activeSpotlightCustomer: payload.activeSpotlightCustomer,
          id: cfaId,
          memberType: CareConstants.spotCustomer
        };
      }),
      catchError((error) => {
        return this.ehs.handleError(error)
      })
    );
  }

  public reopenReport(report) {
    const { id, cfaId } = report;
    const payload = { resolutionStatus: 'Reopen' };

    this.analytics.sendEvent('Reopen Closed Care Contact', {});

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${id}/${cfaId}/response?type=status`;
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map((response) => response),
      catchError(this.ehs.handleError)
    );
  }

  public getCustomersHttp(payload) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = '/api/location/' + locationNumber + '/filters/customers';
        return this.http.post(environment.spotlightApiUrl + url, payload);
      }),
      map((res: any) => res.responseObject || {}),
      catchError((error) => {
        return this.ehs.handleError(error)
      })
    );
  }

  public getCustomerInformation(report) {
    const cfaId = report.cfaId? report.cfaId : report.cfaid;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let baseLocationUrl = '/api/location/';
        let url = baseLocationUrl + loc.locationNumber + '/customer/' + cfaId;
        let req = {};

        return this.http.get(environment.spotlightApiUrl + url, req).pipe(
          map((res) => {
            return res;
          }),
          catchError(this.ehs.handleError)
        );
      })
    );
  }

  public createCustomerNote(note, ticketNumber) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const cfaId = note.cfaUid;
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/notes/${cfaId}?ticket=${ticketNumber}`;
        let req = note.note;
        return this.http.post(environment.spotlightApiUrl + url, req).pipe(
          map((res: any) => {
            this.analytics.sendEvent('CARE: Customer Note Created', {});
            let response = { ...res.responseObject };
            return response || {};
          }),
          catchError((error) => {
            this.analytics.sendEvent('CARE: Create Customer Note Failed', {});
            return this.ehs.handleError(error);
          })
        );
      })
    );
  }

  public updateCustomerNote(note, ticketNumber) {
    const cfaId = note.cfaUid;
    let noteId = note.noteId;
    let locationNumber = note.locationNumber;
    const url = `/api/location/${locationNumber}/notes/${cfaId}/${noteId}?ticket=${ticketNumber}`;
    let req = note.note;

    return this.http.put(environment.spotlightApiUrl + url, req).pipe(
      map((res: any) => {
        this.analytics.sendEvent('CARE: Customer Note Updated', {});
        let response = { ...res.responseObject };
        return response || {};
      }),
      catchError((error) => {
        this.analytics.sendEvent('CARE: Update Customer Note Failed', {});
        return this.ehs.handleError(error);
      })
    );
  }

  public deleteCustomerNote(note) {
    let baseNoteUrl = '/api/location/';
    let url = baseNoteUrl + note.locationNumber + '/notes' + '/' + note.cfaUid + '/' + note.noteId;

    return this.http.delete(environment.spotlightApiUrl + url).pipe(
      map((res: any) => {
        this.analytics.sendEvent('CARE: Customer Note Deleted', {});
        let response = { ...res.responseObject };
        return response || {};
      }),
      catchError((error) => {
        this.analytics.sendEvent('CARE: Delete Customer Note Failed', {});
        return this.ehs.handleError(error);
      })
    );
  }

  public createActionHistoryNote(payload) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const activityId = payload.activityId;
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${activityId}/notes`;
        let req = payload.note;
        return this.http.post(environment.spotlightApiUrl + url, req).pipe(
          map((res: any) => {
            this.analytics.sendEvent('CARE: Action History Note Created', {});
            let response = { ...res.responseObject };
            return response || {};
          }),
          catchError((error) => {
            this.analytics.sendEvent('CARE: Create ActionHistory Note Failed', {});
            return this.ehs.handleError(error);
          })
        );
      })
    );
  }

  public updateActionHistoryNote(payload) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const activityId = payload.activityId;
        const noteId = payload.note.noteId;
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/${activityId}/notes/${noteId}`;
        let req = payload.note;

        return this.http.put(environment.spotlightApiUrl + url, req).pipe(
          map((res: any) => {
            this.analytics.sendEvent('CARE: Action History Note Updated', {});
            const response = { ...res.responseObject };
            return response || {};
          }),
          catchError((error) => {
            this.analytics.sendEvent('CARE: Update Action History Note Failed', {});
            return this.ehs.handleError(error);
          })
        );
      })
    );
  }

  public getEligibleForRecovery(payload) {
    const { id } = payload;
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/reward/eligibility/${id}`;
        return this.http.get(environment.spotlightApiUrl + url, payload);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public getCaseDetails(reportNumber: any) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/2.0/case/${reportNumber}`;
        return this.http.get(environment.spotlightApiUrl + url, reportNumber);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public getRecoveredCfaOneContacts(request) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/recovery/cfaone/closed/contacts`;
        return this.http.post(environment.spotlightApiUrl + url, request);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => {
        return this.ehs.handleError(err);
      })
    );
  }

  public openActionSelectionDialog(report, sendWithOffer: string, navigateToProfile: boolean, originQueryParams: any = {}) {
    let incidentId = report.id;

    let config = new MatDialogConfig();
    config.data = { ...report };
    let dialogRef = this.dialog.open(ActionSelectionDialogComponent, config);

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((responseObj) => {
        if (responseObj) {
          let { isSend, value, note } = responseObj;
          if (isSend) {
            this.analytics.sendEvent(
              'Respond flow started on desktop',
              { IncludeReward: value }
            );

            this.store.dispatch(new care.SelectReport(report));
            this.store.dispatch(new care.SelectTemplate({
              includeReward: value === sendWithOffer,
              memberType: report.membership,
              note
            }));
            this.router.navigate(['/care/respond/respond-details'], { queryParams: { ...originQueryParams, incidentId, note } });
          } else {
            this.analytics.sendEvent(
              'Incident status updated',
              { ChangedTo: value }
            );

            this.store.dispatch(new care.UpdateStatus({
              incidentId,
              note,
              cfaId: report.cfaId,
              resolutionStatus: value,
              type: 'status'
            }));
            if (navigateToProfile) {
              this.router.navigate([`/care/details`], { queryParams: { ...originQueryParams, incidentId, note } });
            }
          }
        }
      });
    }
  }
}
